import React from 'react';
import { AreaChart, Donut, Heatmap, StatCard } from '../charts';
import { Tabbed, Frame, Scene, Card, Line, Chip, Bar, Spark, Avatar, pos, COLORS } from './kit';
import { GateCard } from './loop';

// Active Portal: the full nventr experience on the wall panel, and the one
// gate seen in its four postures. Fixture data only.

const GATES = [
  { id: 'INV-20841', who: 'Acme Industrial', amt: '$12,480', why: 'over $10,000', age: '2 min', pri: 'high', pipe: 'Invoice intake' },
  { id: 'PO-7731 change', who: 'Nordic Fasteners', amt: '$1,920', why: 'quantity 40 → 48', age: '18 min', pri: 'normal', pipe: 'Procurement' },
  { id: 'Supplier onboarding', who: 'Nordic Fasteners', amt: 'W-9 · IBAN', why: 'new payee', age: '1 h 04', pri: 'normal', pipe: 'Supplier onboarding' },
];
const PIPES = [
  ['Invoice intake', 'review gate · INV-20841', 0.62, '38 today', COLORS.amber], ['Supplier onboarding', 'validate · 2 of 4 checks', 0.35, '4 today', COLORS.teal],
  ['Weekly digest', 'done 08:10 · published', 1, '1 today', COLORS.green], ['Launch swarm', 'assemble + refine · round 2 / 2', 0.78, '2 today', COLORS.violet],
  ['Ticket triage', 'classify · 14 in flight', 0.5, '112 today', COLORS.teal], ['Price watch', 'scheduled · 14:00', 0.08, '—', COLORS.muted],
];
const COMMITS = [
  ['09:41', 'erp.post_invoice', 'INV-20840 · $8,120', 'rv_3c9e', 'cm_8f3a21'], ['09:12', 'webhook.announce', 'launch plan r2', 'rv_3c71', 'cm_77be04'],
  ['08:52', 'knowledge.publish', 'weekly digest · internal', 'rv_3c58', 'cm_61a0ff'], ['08:10', 'crm.update_contact', 'Baltic Freight · terms', 'rv_3c40', 'cm_5d12c9'],
];
const LOG = [
  ['09:38:02', 'receive', 'mail · 1 attachment · INV-20841.pdf · 184 KB'], ['09:38:04', 'extract', 'OCR 0.98 · 14 fields · vendor Acme Industrial'],
  ['09:38:06', 'match', 'PO-7731 · 3-way · 98% · duplicate check pass'], ['09:38:06', 'policy', 'approvals-v7 · amount 12,480 > 10,000 → human review'],
  ['09:38:07', 'gate', 'control.review · queue high · assigned ops-leads · waiting'], ['—', 'commit', 'blocked until review · capability erp.post_invoice declared'],
];
const RUN_NODES = [
  { id: 'rx', title: 'Receive invoice', type: 'Mail intake', x: 20, y: 120, st: 'done' }, { id: 'ex', title: 'Extract fields', type: 'LLM · OCR', x: 180, y: 120, st: 'done' },
  { id: 'mt', title: 'Validate & match', type: 'Rule · PO 3-way', x: 340, y: 120, st: 'done' }, { id: 'rv', title: 'Review gate', type: 'control.review · human', x: 500, y: 120, st: 'gate' },
  { id: 'rt', title: 'Route', type: 'Branch · amount', x: 500, y: 240, st: 'queued' }, { id: 'po', title: 'Post to ERP', type: 'Capability · erp.post_invoice', x: 660, y: 120, st: 'queued' },
  { id: 'nt', title: 'Notify requester', type: 'Capability · mail.send', x: 660, y: 240, st: 'queued' },
];
const RUN_EDGES = [['rx', 'ex'], ['ex', 'mt'], ['mt', 'rv'], ['rv', 'po'], ['rv', 'rt', 'reject'], ['rt', 'nt']];
const ATH_SERIES = [120, 132, 128, 150, 162, 171, 158, 180, 201, 196, 214, 230, 228, 244];
const ATH_XY = ATH_SERIES.map((y, x) => ({ x, y }));
const ATH_GRID = Array.from({ length: 7 }, (_, d) => Array.from({ length: 24 }, (_, h) => (h > 7 && h < 19 && d < 5 ? 3 + ((d * 7 + h * 3) % 9) : (h > 9 && h < 17 ? 1 : 0))));

/* ---- shell ----------------------------------------------------------------- */
const RAIL = [['home', 'Home'], ['run', 'nQube'], ['gate', 'Gates', 3], ['agent', 'Agent'], ['athena', 'Athena'], ['apps', 'Apps']];
function Shell({ active, title, crumbs, children, light }) {
  return (
    <div className={`as-portal ${light ? 'light' : ''}`}>
      <nav className="as-rail" aria-hidden="true">
        <span className="as-rail-logo" />
        {RAIL.map(([id, l, n]) => <span key={id} className={`as-rail-i ${id === active ? 'on' : ''}`}><i className={`ri-${id}`} />{n && <b>{n}</b>}<small>{l}</small></span>)}
        <span className="as-spacer" />
        <span className="as-rail-i"><i className="ri-settings" /></span>
      </nav>
      <div className="as-portal-main">
        <div className="as-portal-top">
          <strong>{title}</strong><span className="as-dim">{crumbs}</span>
          <span className="as-spacer" />
          <span className="as-search">⌘K · search runs, gates, documents…</span>
          <Avatar name="Alex" i={0} size={22} /><span className="as-dim">09:41</span>
          <span className="as-btn">← ambient</span>
        </div>
        <div className="as-portal-body">{children}</div>
      </div>
    </div>
  );
}

/* ---- tabs ------------------------------------------------------------------- */
function Home() {
  return (
    <div className="as-grid3 tall">
      <Card title="Pending gates" right="3 · oldest 1 h 04">
        {GATES.map((g) => (
          <div key={g.id} className={`as-gate ${g.pri}`}>
            <div className="as-row"><strong>{g.id}</strong><Chip tone={g.pri === 'high' ? 'warn' : ''}>{g.pri}</Chip></div>
            <span>{g.who} · {g.amt} · {g.why}</span>
            <div className="as-row as-dim"><span>{g.pipe} · {g.age}</span><span className="as-link">Open →</span></div>
          </div>
        ))}
        <span className="as-dim">Approve lands on the runtime, never on glass alone — every commit is receipted.</span>
      </Card>
      <div className="as-col">
        <Card title="Pipelines" right="7 live · 157 runs today">
          {PIPES.map(([a, b, v, c, col]) => <div key={a} className="as-pipe"><Line a={a} b={b} c={c} /><Bar v={v} color={col} /></div>)}
        </Card>
        <Card title="Recent commits" right="ledger">
          {COMMITS.map(([t, cap, what, rv, cm]) => <Line key={cm} a={<code>{cap}</code>} b={`${what} · ${rv}`} c={<code>{cm}</code>} lead={<span className="as-dim as-mono">{t}</span>} />)}
        </Card>
      </div>
      <div className="as-col">
        <Card title="Athena · 7 days" right="preview">
          <div className="as-kpis">
            <div><span className="as-h">Completed</span><strong>1,284</strong><Spark points={ATH_SERIES} /></div>
            <div><span className="as-h">Error rate</span><strong>1.8%</strong><Spark points={[4, 3, 3, 2, 2, 2, 2]} color={COLORS.amber} /></div>
            <div><span className="as-h">Gated commits</span><strong>212</strong><Spark points={[20, 26, 31, 28, 35, 34, 38]} color={COLORS.green} /></div>
            <div><span className="as-h">p95 duration</span><strong>35.1 s</strong><Spark points={[6, 6, 5, 5, 5, 4, 4]} color={COLORS.violet} /></div>
          </div>
        </Card>
        <Card title="Agent threads" right="3 open">
          <Line lead={<Avatar name="Ops" i={1} />} a="Ops assistant" b="“Why was INV-20841 held?” · answered · 2 cites" c="09:40" />
          <Line lead={<Avatar name="Procure" i={2} />} a="Procurement" b="proposal filed → PO-7731 change gate" c="09:22" tone="teal" />
          <Line lead={<Avatar name="Kit" i={3} />} a="Field service app" b="customer asked for a visit window" c="08:57" />
        </Card>
        <Card title="Deliveries" right="3 arriving">
          <Line a="DHL" b="monitor arm" c="14–16" /><Line a="UPS" b="spare filters ×3" c="by 18:00" /><Line a="Courier" b="prototype PCBs" c="11:20" tone="teal" />
        </Card>
      </div>
    </div>
  );
}

function RunGraph({ highlight = 'rv' }) {
  const byId = Object.fromEntries(RUN_NODES.map((n) => [n.id, n]));
  const port = (n, side) => [side === 'out' ? n.x + 140 : n.x, n.y + 26];
  return (
    <svg className="as-rungraph" viewBox="0 0 820 320" aria-hidden="true">
      {RUN_EDGES.map(([a, b, lbl]) => {
        const [x1, y1] = port(byId[a], 'out'); const [x2, y2] = port(byId[b], 'in');
        const d = a === b ? '' : `M${x1} ${y1} C ${x1 + 30} ${y1}, ${x2 - 30} ${y2}, ${x2} ${y2}`;
        const done = byId[a].st === 'done';
        return <g key={a + b}><path d={d} fill="none" stroke={done ? '#14e0c8' : 'rgba(255,255,255,0.25)'} strokeWidth={done ? 2 : 1.2} strokeDasharray={lbl ? '4 4' : undefined} />{lbl && <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 6} className="as-svg-tick" textAnchor="middle">{lbl}</text>}</g>;
      })}
      {RUN_NODES.map((n) => (
        <g key={n.id} transform={`translate(${n.x} ${n.y})`} className={`as-node ${n.st} ${n.id === highlight ? 'hl' : ''}`}>
          <rect width="140" height="52" rx="8" />
          <text x="10" y="20" className="as-node-t">{n.title}</text>
          <text x="10" y="38" className="as-node-s">{n.type}</text>
          <circle cx="128" cy="12" r="4" className="as-node-dot" />
        </g>
      ))}
      <text x="20" y="300" className="as-svg-tick">run r_7f21 · Invoice intake v12 · policy approvals-v7 · started 09:38:02 · waiting on the gate 3 min</text>
    </svg>
  );
}

function Run() {
  return (
    <div className="as-run">
      <div className="as-run-left">
        <div className="as-topband"><strong>Invoice intake</strong><Chip tone="teal">running</Chip><span className="as-dim">run r_7f21 · trigger: mail</span><span className="as-spacer" /><Chip>v12</Chip><Chip>approvals-v7</Chip><span className="as-btn">Open in nQube</span></div>
        <RunGraph />
        <Card title="Run log" right="live">
          <div className="as-log">{LOG.map(([t, k, m]) => <div key={t + k}><span>{t}</span><em>{k}</em><span>{m}</span></div>)}</div>
        </Card>
      </div>
      <div className="as-col">
        <Card title="Inspector · Review gate" right="control.review">
          <Line a="Queue" c="Review · high" /><Line a="Assignees" c="ops-leads (3)" /><Line a="Why" c="amount > $10,000" /><Line a="Can commit" c="no — only the runtime" tone="warn" /><Line a="On reject" c="route → notify requester" /><Line a="Timeout" c="4 h → escalate" />
        </Card>
        <Card title="Declared capabilities" right="2">
          <Line a={<code>erp.post_invoice</code>} b="sandbox · admin-owned · secret in SSM" c="gated" tone="ok" /><Line a={<code>mail.send</code>} b="requester only" c="gated" tone="ok" />
          <span className="as-dim">The validator refuses any graph where a model block reaches either of these ungated.</span>
        </Card>
        <Card title="This run" right="3 min"><Line a="Tokens" c="4,120 · tier bulk" /><Line a="Cost" c="$0.011" /><Line a="Retries" c="0" /></Card>
      </div>
    </div>
  );
}

export function GateReview({ compact, light }) {
  return (
    <div className={`as-review ${compact ? 'compact' : ''} ${light ? 'light' : ''}`}>
      <div className="as-col">
        <Card title="Review · INV-20841" right={<Chip tone="warn">high · 3 min</Chip>}>
          <div className="as-payload">
            {[['vendor', 'Acme Industrial'], ['invoice', 'INV-20841'], ['amount', '12,480.00 USD'], ['due', '2026-10-14'], ['po', 'PO-7731 · matched 98%'], ['cost center', 'OPS-114 · Facilities'], ['terms', 'net 30'], ['tax', '0.00 · reverse charge']].map(([k, v]) => <div key={k}><span>{k}</span><strong className={k === 'amount' ? 'edited' : ''}>{v}</strong></div>)}
          </div>
          <div className="as-diff"><span className="as-dim">edited before approval</span><code><del>cost center: OPS-110</del> → <ins>OPS-114</ins> · by Alex · reason: moved to Facilities</code></div>
          {!compact && (
            <>
              <span className="as-h" style={{ marginTop: 6 }}>Line items · 3</span>
              <Line a="Hex bolts M12 · 48 × 260.00" b="PO line 1 · matched" c="12,480.00" />
              <Line a="Shipping" b="included · PO terms" c="0.00" />
              <Line a="Attachments" b="INV-20841.pdf · 184 KB · delivery note DN-5520" c="2" />
            </>
          )}
        </Card>
        {!compact && (
          <Card title="Evidence" right="5 checks">
            <Line lead={<span className="as-check on" />} a="PO-7731 three-way match" b="qty 48 · unit 260.00 · 98%" c="pass" tone="ok" />
            <Line lead={<span className="as-check on" />} a="Duplicate check" b="no invoice with this number or amount in 180 d" c="pass" tone="ok" />
            <Line lead={<span className="as-check on" />} a="Vendor bank details" b="unchanged since 2024-03" c="pass" tone="ok" />
            <Line lead={<span className="as-check on" />} a="Tax ID" b="matches vendor master · DE811…" c="pass" tone="ok" />
            <Line lead={<span className="as-check warn" />} a="Amount threshold" b="12,480 > 10,000 · approvals-v7 §3" c="needs you" tone="warn" />
          </Card>
        )}
      </div>
      <div className="as-col">
        <Card title="Decision" right="approvals-v7">
          <span className="as-dim">Approving lets the runtime call <code>erp.post_invoice</code> once, with this exact payload. The model cannot.</span>
          <div className="as-btnrow big"><span className="as-btn primary">Approve</span><span className="as-btn">Edit fields</span><span className="as-btn danger">Reject</span></div>
          <Line a="Feedback" b="optional · stored with the review" />
          {light && <Chip tone="teal">confirm on your phone · Face ID</Chip>}
          {!compact && (
            <>
              <Line a="Also may approve" b="Dana · Priya (ops-leads)" c="SLA 4 h · escalates 13:38" />
              <Line a="Then" b="runtime posts once → receipt → requester mailed" c="≈ 1 s" />
              <Line a="Reject" b="stops the branch · requester notified with your note" />
            </>
          )}
        </Card>
        {!compact && (
          <Card title="Prior commits · this vendor" right="ledger · 5 in 12 months">
            {[['2026-09-26', 'INV-20812', '$9,840', 'cm_41be', 'approvals-v6'], ['2026-08-29', 'INV-20777', '$11,200', 'cm_3a90', 'approvals-v6'], ['2026-07-30', 'INV-20740', '$9,120', 'cm_2f11', 'approvals-v5'], ['2026-06-27', 'INV-20702', '$8,760', 'cm_1c58', 'approvals-v5'], ['2026-05-29', 'INV-20661', '$9,300', 'cm_0f9a', 'approvals-v5']].map(([d, inv, amt, cm, pv]) => <Line key={cm} a={inv} b={`${d} · ${pv}`} c={`${amt} · ${cm}`} />)}
            <Line a="Total" b="12 months · all three-way matched · 0 rejections" c="$48,220" tone="teal" />
          </Card>
        )}
      </div>
    </div>
  );
}

function Agent() {
  return (
    <div className="as-agent">
      <div className="as-thread">
        <div className="as-msg me"><Avatar name="Alex" i={0} /><div><strong>Alex</strong><p>Why is INV-20841 held, and has Acme ever been over the threshold before?</p></div></div>
        <div className="as-msg"><Avatar name="Ops" i={1} /><div><strong>Ops assistant</strong><span className="as-dim">reads: ledger, policies, knowledge · writes: none</span>
          <p>It is waiting on the <em>Review gate</em> because the amount (12,480 USD) is above the 10,000 USD threshold in <code>approvals-v7 §3</code> <sup>[1]</sup>. The three-way match against PO-7731 passed at 98% and the duplicate check is clean <sup>[2]</sup>.</p>
          <p>Acme has been over the threshold once before: INV-20777 (11,200 USD) on 2026-08-29, approved by Dana under approvals-v6 and committed as <code>cm_3a90</code> <sup>[3]</sup>.</p>
          <div className="as-cites"><span>[1] policies/approvals-v7 · §3 thresholds</span><span>[2] run r_7f21 · match step</span><span>[3] ledger · cm_3a90</span></div>
        </div></div>
        <div className="as-msg me"><Avatar name="Alex" i={0} /><div><strong>Alex</strong><p>Move it to cost center OPS-114 and approve.</p></div></div>
        <div className="as-msg"><Avatar name="Ops" i={1} /><div><strong>Ops assistant</strong>
          <p>I can’t approve — only you can, and only the runtime commits. I have filed the edit as a proposal on the gate; approve it there and the post goes through once.</p>
          <div className="as-proposal"><span className="as-h">Proposal filed → gate INV-20841</span><Line a="cost center" b="OPS-110 → OPS-114" c="pending your approval" tone="warn" /><div className="as-btnrow"><span className="as-btn primary">Open the gate</span><span className="as-btn">Discard</span></div></div>
        </div></div>
        <div className="as-compose"><span>Ask the ops assistant… voice later · read-only tools by construction</span><span className="as-btn primary">Send</span></div>
      </div>
      <div className="as-col">
        <Card title="Agent · Ops assistant" right="Agent I/O">
          <Line a="Model tier" c="strong · nventr/auto" /><Line a="allowReads" c="ledger · runs · policies · knowledge" tone="ok" /><Line a="allowWrites" c="none — proposals only" tone="warn" /><Line a="Knowledge" c="org · Operations · 1,204 chunks" /><Line a="Guardrails" c="no PII echo · cite or decline" />
        </Card>
        <Card title="Knowledge scopes" right="audience">
          <Line a="Platform" b="nventr.io corpus" c="public" /><Line a="Organization" b="Acme Ops · policies, vendors, SOPs" c="internal" /><Line a="This screen" b="schedules · device runbooks" c="internal" />
        </Card>
        <Card title="Gaps this week" right="2 → start a document"><Line a="freight surcharge rule" b="asked 3× · no citation" c="draft" tone="teal" /><Line a="holiday approver" b="asked 1×" c="draft" /></Card>
      </div>
    </div>
  );
}

function Athena() {
  return (
    <div className="as-athena">
      <div className="as-topband"><strong>Athena · Insights</strong><Chip tone="teal">7 days</Chip><Chip>30 days</Chip><Chip>Operations</Chip><span className="as-spacer" /><Chip>preview</Chip></div>
      <div className="as-kpi-row">
        <StatCard label="Active runs" value={6} spark={[3, 4, 5, 4, 6]} color={COLORS.teal} />
        <StatCard label="Completed · 7 days" value={1284} delta={12} spark={[8, 11, 9, 14, 12]} color={COLORS.green} />
        <StatCard label="Error rate" display="1.8%" value={2} delta={-0.4} deltaGoodWhen="down" color={COLORS.amber} />
        <StatCard label="Gated commits" value={212} spark={[20, 26, 31, 28, 35, 34, 38]} color={COLORS.violet} />
      </div>
      <div className="as-ath-grid">
        <Card title="Runs over time" right="completed · errored" className="span2"><AreaChart height={150} series={[{ name: 'completed', color: COLORS.teal, points: ATH_XY }, { name: 'errored', color: COLORS.red, points: ATH_XY.map((p) => ({ x: p.x, y: Math.round(p.y * 0.02) })) }]} formatX={() => ''} /></Card>
        <Card title="Outcomes"><Donut size={126} thickness={14} centerLabel="runs" slices={[{ label: 'done', value: 86, color: COLORS.green }, { label: 'gated', value: 9, color: COLORS.amber }, { label: 'error', value: 5, color: COLORS.red }]} /></Card>
        <Card title="When the work lands" right="weekday × hour · 1,284 runs" className="span3"><div className="as-heat"><Heatmap grid={ATH_GRID} cell={10} gap={2} /></div></Card>
      </div>
    </div>
  );
}

function Apps() {
  const apps = [
    ['Supplier portal', 'suppliers.acme-ops.apps', 'v2.4 · healthy', 'Kit · 48 suppliers signed in this week', 'ok'],
    ['Field service', 'field.acme-ops.apps', 'v1.9 · healthy · iOS + Android', '312 visits scheduled · offline sync on', 'ok'],
    ['Intake desk', 'intake.acme-ops.apps', 'v0.8 · UAT', 'preview expires in 2 h · 2 PRs in review', 'teal'],
  ];
  return (
    <div className="as-apps">
      <div className="as-grid3">
        {apps.map(([n, url, v, s, t]) => (
          <Card key={n} title={n} right={<Chip tone={t}>{v}</Chip>}>
            <span className="as-mono as-dim">{url}</span><span>{s}</span>
            <div className="as-btnrow"><span className="as-btn primary">Open</span><span className="as-btn">Dev Console</span><span className="as-btn">Operate</span></div>
          </Card>
        ))}
      </div>
      <div className="as-grid2">
        <Card title="Systems of record" right="through declared capabilities only">
          <Line lead={<span className="as-dot ok" />} a="ERP" b="sandbox · 3 capabilities · last sync 09:40" c="erp.post_invoice · read_po · read_vendor" />
          <Line lead={<span className="as-dot ok" />} a="CRM" b="1 capability · last sync 09:38" c="crm.update_contact" />
          <Line lead={<span className="as-dot ok" />} a="Documents" b="object store · 14,201 files · intake watched" c="read · ingest" />
          <Line lead={<span className="as-dot ok" />} a="Mail" b="SMTP · requester notices only" c="mail.send" />
          <Line lead={<span className="as-dot ok" />} a="Calendar" b="2 accounts · read" c="read" />
          <Line lead={<span className="as-dot" />} a="Payroll" b="not connected" c="—" />
          <span className="as-h" style={{ marginTop: 6 }}>Commits by capability · 30 days</span>
          <Line a={<code>erp.post_invoice</code>} b="212 · last 09:41 · 0 retries fired twice" c="212" />
          <Line a={<code>crm.update_contact</code>} b="38 · last 08:10" c="38" />
          <Line a={<code>webhook.announce</code>} b="6 · last 09:12" c="6" />
        </Card>
        <Card title="Open on this screen" right="web portal today · native later">
          <Line a="Invoice INV-20841" b="Documents · PDF · 184 KB" c="open" /><Line a="PO-7731" b="ERP · purchase order" c="open" /><Line a="Acme Industrial" b="CRM · vendor · terms net 30" c="open" />
          <Line a="Delivery note DN-5520" b="Documents · scanned 09:12" c="open" /><Line a="Supplier portal · Nordic" b="Kit · onboarding form · step 3 of 4" c="open" />
          <span className="as-h" style={{ marginTop: 6 }}>Recently opened here</span>
          <Line a="Weekly digest" b="knowledge · internal · 08:52" c="Dana" /><Line a="Launch plan r2" b="knowledge · 09:12" c="Alex" />
          <span className="as-dim">Reads open here; every write still proposes → gate → commit.</span>
        </Card>
      </div>
    </div>
  );
}

const PORTAL_TABS = [
  { id: 'home', label: 'Home', sub: 'gates · pipelines · numbers', title: 'Home', crumbs: 'Acme Ops · Operations workspace', note: 'Touch wakes the portal on the panel. Three gates, seven pipelines, the Athena strip, threads and deliveries in one screen.' },
  { id: 'run', label: 'nQube run', sub: 'graph · log', title: 'Invoice intake · run r_7f21', crumbs: 'nQube · pipelines', note: 'The live run with the review gate highlighted, the inspector and the run log; the declared capabilities sit behind the gate.' },
  { id: 'gate', label: 'Gate review', sub: 'approve · edit · reject', title: 'Review · INV-20841', crumbs: 'Gates · high', note: 'Payload, evidence, the edit before approval, the policy version and the vendor’s prior commits — approve on glass, commit by the runtime.' },
  { id: 'agent', label: 'Agent', sub: 'Agent I/O', title: 'Ops assistant', crumbs: 'Agents · threads', note: 'An agent with reads and no writes: it cites, files a proposal onto the gate, and cannot approve.' },
  { id: 'athena', label: 'Athena', sub: 'numbers', title: 'Athena', crumbs: 'Insights · Operations', note: 'Org analytics drawn with the same zero-dependency chart kit the console uses.' },
  { id: 'apps', label: 'Apps', sub: 'App Kit · systems of record', title: 'Apps and systems', crumbs: 'App Kit · integrations', note: 'App Kit apps and the ERP, CRM and documents behind them, reached only through declared capabilities.' },
];

export function ActivePortal({ frame }) {
  const view = { home: <Home />, run: <Run />, gate: <GateReview />, agent: <Agent />, athena: <Athena />, apps: <Apps /> };
  return (
    <Tabbed frame={frame} tabs={PORTAL_TABS} start={0} interval={5200}>
      {(tab) => (
        <Frame kind="panel" label="wall panel · 27in · active portal · touch">
          <Shell active={tab.id} title={tab.title} crumbs={tab.crumbs}>{view[tab.id]}</Shell>
        </Frame>
      )}
    </Tabbed>
  );
}

/* ---- one gate, four postures ---------------------------------------------- */
function Receipt() {
  const rows = [
    ['commit', 'cm_8f3a21'], ['capability', 'erp.post_invoice · sandbox'], ['payload sha256', '4b1e…9c07 · 1,142 bytes'], ['reviewId', 'rv_3d02 · approved by Alex · 09:41:12'],
    ['policyVersion', 'approvals-v7'], ['idempotencyKey', 'invoice-intake:r_7f21:post'], ['runId', 'r_7f21 · Invoice intake v12'], ['result', 'ERP document 4471 · 200 OK · 412 ms'],
  ];
  return (
    <Frame kind="panel" label="wall panel · the receipt · ambient resumes in 8 s">
      <Scene name="ocean" />
      <div className="as-receipt">
        <span className="as-h">Commit ledger · entry</span>
        <strong>INV-20841 · $12,480 posted to ERP by the runtime</strong>
        <div className="as-receipt-rows">{rows.map(([k, v]) => <div key={k}><span>{k}</span><code>{v}</code></div>)}</div>
        <div className="as-row"><span className="as-dim">A retried run finds this record and does not fire twice.</span><Chip tone="ok">gatedCommits 1,284 → 1,285</Chip></div>
      </div>
      <GateCard posted style={{ position: 'absolute', right: 20, bottom: 20, width: 300 }} />
    </Frame>
  );
}

function Projected() {
  return (
    <>
      <Frame kind="glow" source="pendant" label="the wall mirrors what is being reviewed · nothing is approved by a gesture" style={{ ...pos(0, 0, 760, 585) }}>
        <Scene name="dusk" style={{ opacity: 0.3 }} />
        <GateReview compact light />
      </Frame>
      <div style={pos(780, 10, 250, 560)}>
        <Frame kind="phone" label="phone companion">
          <div className="as-phone-ui">
            <div className="as-phone-top"><span>09:41</span><span>nventr · Acme Ops</span></div>
            <span className="as-h">Review · high</span>
            <strong>INV-20841 · Acme Industrial</strong>
            <span className="as-big">$12,480.00</span>
            <div className="as-payload small">{[['po', 'PO-7731 · 98%'], ['due', '14 Oct'], ['cost center', 'OPS-114 · edited'], ['policy', 'approvals-v7 §3']].map(([k, v]) => <div key={k}><span>{k}</span><strong>{v}</strong></div>)}</div>
            <span className="as-h">Evidence</span>
            <Line lead={<span className="as-check on" />} a="3-way match" c="pass" tone="ok" />
            <Line lead={<span className="as-check on" />} a="Duplicate" c="pass" tone="ok" />
            <Line lead={<span className="as-check on" />} a="Bank details" c="pass" tone="ok" />
            <Line lead={<span className="as-check warn" />} a="Threshold" c="needs you" tone="warn" />
            <Chip tone="teal">mirrored · dining wall · bulb</Chip>
            <span className="as-dim">Approving lets the runtime post this once. Nothing on the wall is a control.</span>
            <span className="as-spacer" />
            <span className="as-btn primary block">Approve · Face ID</span>
            <div className="as-btnrow"><span className="as-btn">Edit</span><span className="as-btn danger">Reject</span></div>
          </div>
        </Frame>
      </div>
    </>
  );
}

const GATE_TABS = [
  { id: 'card', label: 'Card in ambient', sub: 'pending', note: 'The pending-gate card pulls forward over the loop without leaving ambient. One line: who, how much, why, how long.' },
  { id: 'review', label: 'Full review', sub: 'Active Portal', note: 'Touch opens the full review: payload, evidence, the edit, the policy version, prior commits — approve, edit or reject.' },
  { id: 'receipt', label: 'Commit receipt', sub: 'ledger', note: 'Only the runtime commits, once, and writes the receipt: payload hash, review id, policy version, idempotency key.' },
  { id: 'projected', label: 'Projected wall + phone', sub: 'no touch', throw: 'pendant', note: 'On a projected surface the wall mirrors the review and the phone is the companion; writes are confirmed on the phone, never by a gesture at a wall.' },
];

export function GateOnGlass({ frame }) {
  return (
    <Tabbed frame={frame} tabs={GATE_TABS} start={1} interval={4800} h={585}>
      {(tab) => {
        if (tab.id === 'card') {
          return (
            <Frame kind="panel" scene="ocean" label="wall panel · ambient loop · evening">
              <div className="as-ambient-min"><span className="as-clock-time" style={{ fontSize: 56 }}>18:36</span><span className="as-clock-date">Tuesday · sunset 18:42 · 15°</span></div>
              <GateCard style={{ position: 'absolute', right: 24, bottom: 24, width: 340 }} />
            </Frame>
          );
        }
        if (tab.id === 'review') {
          return <Frame kind="panel" label="wall panel · active portal"><Shell active="gate" title="Review · INV-20841" crumbs="Gates · high · 3 min"><GateReview /></Shell></Frame>;
        }
        if (tab.id === 'receipt') return <Receipt />;
        return <Projected />;
      }}
    </Tabbed>
  );
}